import { useEffect, useState } from 'react';

export type AdminView =
  'admin' | 'admin/new' | 'admin/values' | 'admin/audit' | `admin/champion/${string}`;
export type View =
  | 'play'
  | 'login'
  | 'register'
  | 'account'
  | 'stats'
  | 'settings'
  | 'how-to-play'
  | `game/${string}`
  | AdminView;

const VIEWS: readonly View[] = [
  'play',
  'login',
  'register',
  'account',
  'stats',
  'settings',
  'how-to-play',
];
const ADMIN_VIEWS: readonly View[] = ['admin', 'admin/new', 'admin/values', 'admin/audit'];
const GAME_ID = /^game\/[0-9a-f-]{36}$/i;
const ADMIN_CHAMPION = /^admin\/champion\/[A-Za-z0-9]{1,40}$/;

const read = (): View => {
  const name = window.location.hash.replace(/^#\/?/, '');
  if (GAME_ID.test(name) || ADMIN_CHAMPION.test(name)) return name as View;
  if (ADMIN_VIEWS.includes(name as View)) return name as View;
  return VIEWS.find((v) => v === name) ?? 'play';
};

/** Hash routing keeps the server's job to one static page, and back/forward still work. */
export function useView(): [View, (v: View) => void] {
  const [view, setView] = useState<View>(read);
  useEffect(() => {
    const onChange = (): void => setView(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const go = (v: View): void => {
    window.location.hash = v === 'play' ? '' : `/${v}`;
    setView(v);
  };
  return [view, go];
}

export const isAdminView = (v: View): v is AdminView => v === 'admin' || v.startsWith('admin/');
